import {
  DialogActions,
  DialogTitle,
  Icon,
  OutlinedInput,
} from '@aibees/shared/components';
import { useEditCustomerMutationApi } from '@aibees/shared/features/billing-profile/api/use-edit-customer-mutation-api';
import { useReadCustomerApi } from '@aibees/shared/features/billing-profile/api/use-read-customer-api';
import { useBoolean, useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import {
  Button,
  Dialog,
  DialogContent,
  Fade,
  Stack,
  Typography,
} from '@mui/material';
import { memo, useCallback, useEffect, useState } from 'react';
import { string as stringTest } from 'yup';
import * as Styled from './styles';
import * as SX from './sx';
import type { EmailSettingsHeaderProps as Props } from './types';
export const EmailSettingsHeader = memo(
  ({ className, currentEmail, setCurrentEmail }: Props) => {
    const notify = useNotify();
    const workspaceId = useWorkspace()?.id;
    const { data } = useReadCustomerApi(workspaceId || '');
    const dialog = useBoolean(false);
    const [newEmail, setNewEmail] = useState('');
    const isEmailChanged = currentEmail !== newEmail;

    useEffect(() => {
      if (data?.email) {
        setCurrentEmail(data.email);
        setNewEmail(data.email);
      }
    }, [data?.email]);

    const isValidEmail = stringTest().email().required().isValidSync(newEmail);
    const api = useEditCustomerMutationApi(workspaceId || '');

    const handleOpen = useCallback(() => {
      setNewEmail(currentEmail || '');
      dialog.onTrue();
    }, [currentEmail, dialog]);

    const handleClose = useCallback(() => {
      setNewEmail(currentEmail || '');
      dialog.onFalse();
    }, [currentEmail, dialog]);

    const handleSubmit = useCallback(async () => {
      if (!isValidEmail) {
        notify.error('Invalid email address');
        return;
      }
      if (!data) {
        notify.error('No data available');
        return;
      }

      try {
        await api.mutateAsync({
          ...data,
          email: newEmail.trim(),
          name: data.name || '',
          address: data.address || {
            country: '',
            state: '',
            city: '',
            line1: '',
            line2: '',
            postalCode: '',
          },
        });
        notify.success('Email changed successfully');
        setCurrentEmail(newEmail.trim());
        dialog.onFalse();
      } catch {
        notify.error('Error changing email');
      }
    }, [api, data, newEmail, isValidEmail, notify, dialog, setCurrentEmail]);

    return (
      <Styled.Wrapper
        className={classnames('EmailSettingsHeader', className)}
        sx={SX.root}
      >
        <Stack direction="row" gap={0.5}>
          <Icon name="email" size={20} color="grey.700" />
          <Stack>
            <Typography variant="body4" fontWeight={500} color="grey.600">
              Email Settings
            </Typography>
            <Typography variant="body5" fontWeight={400} color="grey.500">
              Your email address for billing communications
            </Typography>
          </Stack>
        </Stack>

        <Button variant="outlined" size="xsmall" onClick={handleOpen}>
          Change Email
        </Button>

        <Dialog
          open={dialog.value}
          maxWidth="xs"
          fullWidth
          TransitionComponent={Fade}
          onClose={handleClose}
        >
          <DialogTitle
            title="Change Email Billing address"
            icon="email"
            onClose={handleClose}
          />

          <DialogContent>
            <Stack gap={1}>
              <Typography variant="body3" color="grey.800" fontWeight={500}>
                This email address can only be changed with the confirmation of
                the workspace owner.
              </Typography>
              <Typography variant="body4" color="grey.500" fontWeight={400}>
                Current Billing Email
              </Typography>
            </Stack>

            <OutlinedInput
              name="name"
              size={'small'}
              value={newEmail}
              placeholder="Please enter a New Email Address"
              onChange={(e) => setNewEmail(e.currentTarget.value)}
              autoComplete="off"
              fullWidth
            />
          </DialogContent>

          <DialogActions
            submitTitle="Change Address"
            disabled={!isEmailChanged || !isValidEmail || api.isLoading}
            loading={api.isLoading}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </Dialog>
      </Styled.Wrapper>
    );
  }
);

EmailSettingsHeader.displayName = 'EmailSettingsHeader';
